import React from 'react';
import { formatMoney } from 'accounting-js';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Link from '../../utils/link';
import Typography from '../../atoms/typography';

const Brand = styled(Typography)`
  color: ${(props) => props.theme.color.brandLight};
  cursor: pointer;
  margin-bottom: 0.2em;
`;

const Title = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
`;

const Price = styled(Typography)`
  color: ${({ theme }) => theme.color.brand};
  justify-content: center;
  letter-spacing: normal;
  line-height: 2.2rem;
  margin-top: 1rem;
  text-transform: uppercase;
`;

const NoUnderlineAnchor = styled.a`
  text-decoration: none;
`;

const Header = styled.header`
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  align-content: center;

  ${Brand}, ${Title}, ${Price} {
    line-height: 1;
    text-align: center;
  }
`;

const OptionInvalid = styled.span`
  color: ${(props) => props.theme.color.brandError};
`;

const ProductHeader = (props) => {
  const getVendorPrices = () => props.variant.prices.filter((v) => v.vendor_id === props.vendor);

  const getPrice = () => {
    const priceArray = props.variant ? getVendorPrices() : props.prices;
    const prices = priceArray.map(
      (p) => ({ original_price: p?.original_price ?? 0, price: p?.price ?? 0 })
    );
    const sales = priceArray.filter((p) => p?.on_sale || false);

    const final = { has_sale: false, discount: null };

    const getMin = (arr) => arr.reduce((acc, p) => {
      const a = acc;

      if (+p.price < +a.price && +p.price > 0) {
        a.original_price = +p.original_price;
        a.price = +p.price;
      }

      return a;
    }, {
      original_price: Math.min.apply(null, arr.map((p) => p.original_price).filter(Boolean)),
      price: Math.min.apply(null, arr.map((p) => p.price).filter(Boolean))
    });

    const getMax = (arr) => arr.reduce((acc, p) => {
      const a = acc;

      if (+p.price > +a.price) {
        a.original_price = +p.original_price;
        a.price = +p.price;
      }

      return a;
    }, { original_price: arr[0].original_price, price: arr[0].price });

    if (sales.length > 0) {
      const percents = sales.map((s) => Math.round(s.discount_percent));

      if (percents.every((p) => p === percents[0])) {
        final.discount = `${Math.round(percents[0])}% Off`;
      } else {
        final.discount = `Up to ${Math.max(...percents)}% Off`;
      }

      final.has_sale = true;
      final.max = getMax(sales);
      final.min = getMin(sales);
    } else {
      final.min = prices.length ? getMin(prices) : { price: 0 };
      final.max = prices.length ? getMax(prices) : { price: 0 };
    }

    return final;
  };

  const price = getPrice();

  return (
    <Header>
      {
        props.brand && (
          <>
            {
              props.brandSlug ? (
                <Link href={`/brands/${props.brandSlug}`} passHref>
                  <NoUnderlineAnchor>
                    <Brand element="h2" like="heading-3">{props.brand}</Brand>
                  </NoUnderlineAnchor>
                </Link>
              ) : (
                <Brand element="h2" like="heading-3">{props.brand}</Brand>
              )
            }
          </>
        )
      }

      <Link
        href={`/product/${props.slug}`}
        passHref
      >
        <NoUnderlineAnchor>
          <Title element="h1" like="heading-3">{props.title}</Title>
        </NoUnderlineAnchor>
      </Link>

      <Price tabIndex="0" element="div" like="dec-1">
        {
          props.noOffers || price.min.price + price.max.price === 0
            ? <OptionInvalid>Out of Stock</OptionInvalid>
            : (
              <>
                {price.has_sale && (
                  <>
                    {price.min.price !== price.max.price && 'From '}
                    <Price
                      element="span"
                      like="label-1"
                      css={(theme) => ({
                        textDecoration: 'line-through',
                        color: theme.color.brand
                      })}
                    >
                      {formatMoney(price.min.original_price)}

                    </Price>
                    {' '}
                    {formatMoney(price.min.price)}
                    {' | '}
                    <Price
                      element="span"
                      like="label-1"
                      css={(theme) => ({
                        color: theme.color.brandA11yRed
                      })}
                    >
                      {price.discount}

                    </Price>
                  </>
                )}

                {!price.has_sale && (
                  <>
                    {
                      +price.min.price === +price.max.price
                        ? formatMoney(price.min.price)
                        : `${formatMoney(price.min.price)} - ${formatMoney(price.max.price)}`
                    }
                  </>
                )}
              </>
            )
        }
      </Price>
    </Header>
  );
};

ProductHeader.defaultProps = {
  title: '',
  brand: '',
  brandSlug: '',
  slug: '',
  variant: false,
  vendor: 0,
  noOffers: false
};

ProductHeader.propTypes = {
  brand: PropTypes.string,
  brandSlug: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  prices: PropTypes.array.isRequired,
  variant: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  vendor: PropTypes.number,
  noOffers: PropTypes.bool
};

ProductHeader.whyDidYouRender = true;

export default ProductHeader;
